/**
 * Static Data Store
 * Provides lookup data (database types) and delegates venue data to venueDataStore.
 * This prevents duplicate fetching of venues/levels/spaces/buildings.
 */

import { ref, computed } from "vue";
import { defineStore } from "pinia";
import { useEdgeApi } from "@/composables/useEdgeApi";
import { IncidentSeverityOrder } from "@/constants.js";
import { useVenueDataStore } from "./venueData";
import { NotificationType } from "@/types/api";
import type {
    Venue,
    Level,
    Building,
    Space,
    NotificationTemplate,
    CommunityRole,
} from "@/types/api";

export const useStaticDataStore = defineStore("staticData", () => {
    const edgeApi = useEdgeApi();
    const venueDataStore = useVenueDataStore();

    // ================================================================
    // STATE - Database Types (lookup values)
    // ================================================================

    const deviceTypes = ref<string[]>([]);
    const incidentTypes = ref<any[]>([]);
    const incidentStates = ref<string[]>([]);
    const exposureLevelTypes = ref<any[]>([]);
    const notificationTypes = ref<string[]>([]);
    const notificationStates = ref<string[]>([]);
    const markerTypes = ref<string[]>([]);
    const eventTypes = ref<string[]>([]);
    const deviceOperationStates = ref<string[]>([]);
    const communityRoles = ref<CommunityRole[]>([]);
    const notificationTemplates = ref<NotificationTemplate[]>([]);

    // ================================================================
    // STATE - Loading
    // ================================================================

    const loading = ref(false);
    const error = ref<string | null>(null);
    const dataReady = ref(false);

    // ================================================================
    // COMPUTED - Venue Data (delegated to venueDataStore)
    // ================================================================

    // Use quick reference lists (always complete, even during background loading)
    const venues = computed<Venue[]>(() => venueDataStore.allVenuesList);
    const levels = computed<Level[]>(() => venueDataStore.allLevelsList);

    // Buildings and spaces are loaded per-level on demand
    const buildings = computed<Building[]>(() => {
        void venueDataStore.levelDataVersion;
        const all: Building[] = [];
        venueDataStore.venues.forEach((vd) => {
            vd.levelData?.forEach((ld) => {
                if (ld.buildings) all.push(...ld.buildings);
            });
        });
        return all;
    });

    const spaces = computed<Space[]>(() => {
        void venueDataStore.levelDataVersion;
        const all: Space[] = [];
        venueDataStore.venues.forEach((vd) => {
            all.push(...vd.venueSpaces);
            vd.levelData?.forEach((ld) => {
                if (ld.spaces) {
                    ld.spaces.forEach((space) => {
                        if (!all.some((s) => s.id === space.id)) {
                            all.push(space);
                        }
                    });
                }
            });
        });
        return all;
    });

    // ================================================================
    // ACTIONS
    // ================================================================

    async function fetchAllStaticData(): Promise<void> {
        if (dataReady.value) return;

        loading.value = true;
        error.value = null;

        try {
            // Fetch database types and notification templates
            const [{ data: databaseTypes }, { data: templatesData }] =
                await Promise.all([
                    edgeApi.get("database-types"),
                    edgeApi.get("notification-templates"),
                ]);

            // Assign database types
            deviceTypes.value = databaseTypes?.deviceTypes || [];
            incidentTypes.value = databaseTypes?.incidentTypes || [];
            incidentStates.value = databaseTypes?.incidentStates || [];
            exposureLevelTypes.value = databaseTypes?.exposureLevelTypes || [];
            notificationTypes.value = databaseTypes?.notificationTypes || [];
            notificationStates.value = databaseTypes?.notificationStates || [];
            markerTypes.value = databaseTypes?.markerTypes || [];
            eventTypes.value = databaseTypes?.eventTypes || [];
            deviceOperationStates.value =
                databaseTypes?.deviceOperationStates || [];
            communityRoles.value = databaseTypes?.communityRoles || [];

            // Notification templates
            notificationTemplates.value = templatesData || [];

            dataReady.value = true;
        } catch (e: any) {
            error.value = e.message;
            console.error("Failed to fetch static data:", e);
        } finally {
            loading.value = false;
        }
    }

    // Called after template create/update/delete so dropdowns don't go stale
    async function refreshNotificationTemplates(): Promise<void> {
        try {
            const { data } = await edgeApi.get("notification-templates");
            notificationTemplates.value = data || [];
        } catch (e: any) {
            console.error("Failed to refresh notification templates:", e);
        }
    }

    // ================================================================
    // HELPERS
    // ================================================================

    const getExposureStringFromLevel = (numericLevel: number): string => {
        if (exposureLevelTypes.value?.length) {
            const exposureType = exposureLevelTypes.value.find(
                (type: any) => type.level === numericLevel,
            );
            if (exposureType) return exposureType.value;
        }

        const fallbackType = IncidentSeverityOrder.find(
            (type: any) => type.level === numericLevel,
        );
        return fallbackType?.value || "unknown";
    };

    /**
     * Get level name by ID
     * Uses existing levels array from store
     */
    const getLevelName = (levelId: number | null | undefined): string => {
        if (!levelId) return "";
        return levels.value.find((l) => l.id === levelId)?.name || "";
    };

    /**
     * Get space name by ID
     * Uses existing spaces array from store
     */
    const getSpaceName = (spaceId: number | null | undefined): string => {
        if (!spaceId) return "";
        return spaces.value.find((s) => s.id === spaceId)?.name || "";
    };

    /** "Camera - Space (Level 1)"; the level is left off in single-level venues. */
    const getCameraLabel = (camera: {
        name?: string | null;
        levelId?: number | null;
        spaceId?: number | null;
    }): string => {
        let label = [camera.name, getSpaceName(camera.spaceId)]
            .filter(Boolean)
            .join(" - ");

        const level = levels.value.find((l) => l.id === camera.levelId);
        const venueLevelCount = level
            ? levels.value.filter((l) => l.venueId === level.venueId).length
            : 0;
        if (level?.name && venueLevelCount > 1) {
            label += ` (${level.name})`;
        }
        return label;
    };

    const getSpacesByVenueId = (venueId: number | null | undefined): Space[] => {
        if (!venueId) return [];
        return spaces.value.filter((s) => s.venueId === venueId);
    }
    
    const getVenue = (venueId: number | null | undefined): Venue | null =>
        venueId == null
            ? null
            : (venues.value.find((v) => v.id === venueId) ?? null);

    const getVenueName = (venueId: number | null | undefined): string => {
        if (!venueId) return "";
        return venues.value.find((v) => v.id === venueId)?.name || "";
    };

    /**
     * Get building name by ID
     * Uses existing buildings array from store
     */
    const getBuildingName = (buildingId: number | null | undefined): string => {
        if (!buildingId) return "";
        return buildings.value.find((b) => b.id === buildingId)?.name || "";
    };

    /** Get exposure level definition by level number */
    const getExposureLevelDefinition = (level: number | undefined) => {
        if (level === undefined) return null;
        return (
            exposureLevelTypes.value.find(
                (type: any) => type.level === level,
            ) || null
        );
    };

    /** Check if exposure level matches a specific value (data-driven) */
    const isExposureLevel = (
        level: number | undefined,
        exposureValue: string,
    ): boolean => {
        const exposureDefinition = getExposureLevelDefinition(level);
        return exposureDefinition?.value === exposureValue;
    };

    /** Check if exposure level is "offsite" */
    const isOffsite = (level: number | undefined): boolean => {
        return isExposureLevel(level, "offsite");
    };

    /** Check if exposure level is "safe" */
    const isSafe = (level: number | undefined): boolean => {
        return isExposureLevel(level, "safe");
    };

    /** Icon colour for an exposure level (`enduser-color-*` suffix); off-site is blue */
    const getExposureColour = (level: number | undefined): string => {
        if (isOffsite(level)) return "blue";
        return getExposureLevelDefinition(level)?.colour || "black";
    };

    /** Check if an incident type has the evacuate flag */
    const isEvacuatableType = (type: string): boolean => {
        return incidentTypes.value.find((t) => t.value === type)?.evacuate === true;
    };

    // ================================================================
    // GETTERS (backward compatible)
    // ================================================================

    const getVenues = computed(() => venues.value);
    const getLevels = computed(() => levels.value);
    const getBuildings = computed(() => buildings.value);
    const getSpaces = computed(() => spaces.value);
    // System templates are backend-only; never offer them when sending.
    const getNotificationTemplates = computed(() =>
        notificationTemplates.value.filter(
            (template) => template.type !== NotificationType.SYSTEM,
        ),
    );
    const getDeviceTypes = computed(() => deviceTypes.value);
    const getIncidentTypes = computed(() => incidentTypes.value);
    const getIncidentStates = computed(() => incidentStates.value);
    const getExposureLevelTypes = computed(() => exposureLevelTypes.value);
    const getNotificationTypes = computed(() => notificationTypes.value);
    const getNotificationStates = computed(() => notificationStates.value);
    const getMarkerTypes = computed(() => markerTypes.value);
    const getEventTypes = computed(() => eventTypes.value);
    const getDeviceOperationStates = computed(
        () => deviceOperationStates.value,
    );
    const getCommunityRoles = computed(() => communityRoles.value);
    const isLoadingStaticData = computed(() => loading.value);
    const getStaticDataError = computed(() => error.value);

    // ================================================================
    // RETURN
    // ================================================================

    return {
        // State
        venues,
        levels,
        buildings,
        spaces,
        notificationTemplates,
        deviceTypes,
        incidentTypes,
        incidentStates,
        exposureLevelTypes,
        notificationTypes,
        notificationStates,
        markerTypes,
        eventTypes,
        deviceOperationStates,
        communityRoles,
        loading,
        error,
        dataReady,

        // Actions
        fetchAllStaticData,
        refreshNotificationTemplates,

        // Getters
        getVenues,
        getLevels,
        getBuildings,
        getSpaces,
        getNotificationTemplates,
        getDeviceTypes,
        getIncidentTypes,
        getIncidentStates,
        getExposureLevelTypes,
        getNotificationTypes,
        getNotificationStates,
        getMarkerTypes,
        getEventTypes,
        getDeviceOperationStates,
        getCommunityRoles,
        isLoadingStaticData,
        getStaticDataError,

        // Helpers
        getExposureStringFromLevel,
        getLevelName,
        getSpaceName,
        getCameraLabel,
        getSpacesByVenueId,
        getVenue,
        getVenueName,
        getBuildingName,

        // Exposure Level Helpers (data-driven)
        getExposureLevelDefinition,
        isExposureLevel,
        isOffsite,
        isSafe,
        getExposureColour,
        isEvacuatableType,
    };
});
